// Ortak giriş akışı.
//
// Giriş üç ayrı yerden yapılabiliyor: üye giriş sayfası (/giris), yönetim giriş
// sayfası (/yonetim/giris) ve anasayfadaki harita yanındaki hızlı giriş kartı.
// Jeton saklama, önbellek temizleme ve yönlendirme mantığı üçünde de aynı
// olduğu için tek yerde durur.
//
// "scope" hangi kapıdan girildiğini sunucuya bildirir: yönetim hesabı üye
// kapısından, üye hesabı yönetim kapısından giriş yapamaz. Sunucu bu durumda
// 403 ile doğru sayfanın yolunu (loginPath) döndürür; kullanıcıya oraya giden
// bir bağlantı gösterilir.
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../UserContext";
import { getManagementRouteForRole, isManagementRole } from "../utils/roleManagement";
import api, { clearAuthToken, setAuthToken } from "../api/api";
import { isDesktopApp } from "../utils/desktopApp";
import { TOOLS_PATH } from "../editor_pages/adminNav";

export const LOGIN_SCOPES = { MANAGEMENT: "management", MEMBER: "member" };

// Giriş sonrası hedef: yönetim rolleri kendi yönetim merkezine, üyeler panele.
// isManagement alanı sunucudan gelir; eski bir oturum önbelleğinde yoksa rol
// adına bakılır.
export const routeAfterLogin = (user) => {
  if (!user) return "/giris";
  const management = user.isManagement ?? isManagementRole(user.role);
  // Mac masaüstü uygulaması bir araçlar uygulamasıdır: Araçlar Merkezi açılır.
  if (management && isDesktopApp()) return TOOLS_PATH;
  return management ? getManagementRouteForRole(user.role) : "/panel";
};

export default function useLogin({ scope, onSuccess } = {}) {
  const navigate = useNavigate();
  const { setUser } = useUser();

  const [errorMessage, setErrorMessage] = useState("");
  // Yanlış kapıdan giriş denendiğinde gösterilecek yönlendirme bağlantısı.
  const [redirectHint, setRedirectHint] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetError = useCallback(() => {
    setErrorMessage("");
    setRedirectHint(null);
  }, []);

  const submit = useCallback(
    async ({ username, password }) => {
      if (isSubmitting) return null;

      setIsSubmitting(true);
      resetError();

      try {
        // Önceki oturumun kalıntısı yeni girişe karışmasın.
        localStorage.removeItem("user");
        clearAuthToken();
        setUser(null);

        const { data } = await api.post("/api/users/login", {
          username: String(username || "").trim(),
          password,
          scope,
          // Masaüstü uygulaması 90 günlük oturum alır (sunucu UA'dan da tanır).
          ...(isDesktopApp() ? { client: "desktop" } : {}),
        });

        const user = data?.user;
        if (!user) throw new Error("Giriş yanıtında kullanıcı bilgisi yok.");

        // Çerez engellenirse istek başlığında kullanılacak yedek jeton.
        setAuthToken(data?.token);
        setUser(user);
        localStorage.setItem("user", JSON.stringify(user));

        const target = onSuccess ? onSuccess(user) : routeAfterLogin(user);
        if (target) navigate(target, { replace: true });
        return user;
      } catch (error) {
        const body = error?.response?.data || {};
        setErrorMessage(body.message || "Kullanıcı adı veya şifre hatalı.");
        if (body.loginPath) {
          setRedirectHint({
            to: body.loginPath,
            label: body.code === "ADMIN_LOGIN_REQUIRED" ? "Yönetim girişine git" : "Üye girişine git",
          });
        }
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, navigate, onSuccess, resetError, scope, setUser]
  );

  return { submit, errorMessage, redirectHint, isSubmitting, resetError };
}
